import React from 'react';
import PropTypes from 'prop-types';
import { StructFlex, StructItem } from '@cybs-ui/components/lib/struct-flex';
import stylesBuilder from './component.styles';

/**
 * A simple card component for wrapping content.
 */
const CybsCard = React.forwardRef(
  (
    {
      bodyMaxHeight,
      bodyStyle,
      children,
      className,
      dataTest,
      isFocusable,
      footer,
      header,
      i18n: i18nProp,
      isCollapsed,
      isScrollable,
      style,
      ariaRole
    },
    ref
  ) => {
    // Use localization defaults for undefined strings
    const i18n = {
      ...CybsCard.defaultProps.i18n,
      ...i18nProp
    };

const isFooterInsideCard = !!footer;

let label = i18n.card;
    if (i18n.ariaLabel) {
      label = i18n.ariaLabel;
    } else if (header?.props?.i18n?.title) {
      label = header.props.i18n.title;
    }

    const { cardWrapperStyles, cardBodyStyles } = stylesBuilder({
      bodyMaxHeight,
      isCollapsed,
      isScrollable,
      isFooterInsideCard
    });

    const Body = bodyChildren => (
      <StructItem
        css={cardBodyStyles}
        style={bodyStyle}
        tabindex={isFocusable ? 0 : undefined}
        aria-label={isFocusable ? `${label} ${i18n.region}` : undefined}
      >
        {bodyChildren}
      </StructItem>
    );

    Body.displayName = 'Body';

    return (
      <StructFlex
        aria-label={label}
        className={className}
        css={cardWrapperStyles}
        data-test={`${dataTest}-cybsCard`}
        flex-direction="column"
        role={ariaRole}
        style={style}
        ref={ref}
      >
        {header}
        {Body(children)}
        {!isCollapsed && footer}
      </StructFlex>
    );
  }
);

CybsCard.propTypes = {
  /**
   * Set a max-height on body section of card. This should be used with `scrollable`
   */
  bodyMaxHeight: PropTypes.number,

  /**
   * Applies custom styles to the body of the card
   */
  bodyStyle: PropTypes.objectOf(PropTypes.any),

  /**
   * Render content inside the Card.
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * Sets the data-test attribute
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * Footer config object
   */
  footer: PropTypes.element,

  /**
   * Action bar config object
   */
  header: PropTypes.element,

  /*
   * Strings for the component
   */
  i18n: PropTypes.shape({
    ariaLabel: PropTypes.string,
    card: PropTypes.string,
    region: PropTypes.string
  }),

  /**
   * Whether or not the card is collapsed
   */
  isCollapsed: PropTypes.bool,

  /**
   * Indicates if the card will be focusable
   */
  isFocusable: PropTypes.bool,

  /**
   * Allow the body section to scroll. Should be used with `bodyMaxHeight`
   */
  isScrollable: PropTypes.bool,

  /**
   * @ignore
   */
  style: PropTypes.objectOf(PropTypes.any),

  /**
   * Specify the aria role
   */
  ariaRole: PropTypes.string
};

CybsCard.defaultProps = {
  header: null,
  children: [],
  className: '',
  bodyMaxHeight: 300,
  bodyStyle: {},
  footer: null,
  i18n: {
    ariaLabel: '',
    region: 'region',
    card: 'card'
  },
  isCollapsed: false,
  isFocusable: false,
  isScrollable: false,
  style: {},
  ariaRole: 'region'
};

CybsCard.displayName = 'CybsCard';

export default CybsCard;
